import { useState } from 'react';
import {
  Card, CardContent,
} from '@eriko/ui/src/components/ui/card';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Plus, Trash2 } from 'lucide-react';
import { useContractors, useDeleteContractor } from '../../../../hooks/useTax';
import { AddContractorDialog } from './AddContractorDialog';

export function ContractorsSubTab() {
  const { data: contractors, isLoading } = useContractors();
  const deleteContractor = useDeleteContractor();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-surface-700">
          Contractors ({contractors?.length ?? 0})
        </h3>
        <Button size="sm" onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add contractor
        </Button>
      </div>

      {(!contractors || contractors.length === 0) ? (
        <Card>
          <CardContent className="py-8 text-center text-surface-500 text-sm">
            No contractors yet. Add one to start tracking 1099 payments.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {[...contractors].sort((a, b) => a.legalName.localeCompare(b.legalName)).map((c) => (
            <Card key={c.id}>
              <CardContent className="flex items-center justify-between py-3 px-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-surface-900">
                      {c.legalName}
                    </span>
                    {c.tradingName && (
                      <span className="text-xs text-surface-500">({c.tradingName})</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    {c.tinLast4 ? (
                      <span className="text-xs text-surface-500">
                        TIN: ***-**-{c.tinLast4}
                      </span>
                    ) : (
                      <span className="text-xs text-surface-400">No TIN</span>
                    )}
                    {c.hasW9 ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        W-9
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        No W-9
                      </Badge>
                    )}
                    {c.isExemptFrom1099 && (
                      <Badge variant="outline" className="text-xs">
                        1099 Exempt
                      </Badge>
                    )}
                  </div>
                </div>
                {confirmDeleteId === c.id ? (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={deleteContractor.isPending}
                      onClick={() => {
                        deleteContractor.mutate(c.id, {
                          onSettled: () => setConfirmDeleteId(null),
                        });
                      }}
                    >
                      {deleteContractor.isPending ? 'Removing...' : 'Confirm'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmDeleteId(c.id)}
                    className="text-surface-400 hover:text-red-600"
                    aria-label="Delete contractor"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showAddDialog && (
        <AddContractorDialog onClose={() => setShowAddDialog(false)} />
      )}
    </div>
  );
}
